import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProfile, createInvitado, getMyInvitados, getUpcomingEvents } from '../api/api';
import { SkeletonPage, EVENT_TYPE_LABELS, EVENT_TYPE_META, formatEventDate, MexPhoneInput, isValidMxPhone } from '../components/Charts';
import { uiToastSuccess, uiToastError } from '../components/Toast';

const STATUS_LABELS = {
  pendiente: { label: 'Pendiente de revisión', tone: 'warning' },
  aprobado: { label: 'Aprobado', tone: 'success' },
  rechazado: { label: 'Rechazado', tone: 'danger' },
};

const EMPTY_FORM = { name: '', business_name: '', giro: '', phone: '', email: '', notes: '' };

function Invitados() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invitados, setInvitados] = useState([]);
  const [juntas, setJuntas] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getProfile()
      .then(res => {
        setProfile(res.data);
        loadInvitados();
        // Antes solo dejaba ligar a "junta_mensual" a fuerza — ahora cualquier tipo de evento
        // que Admin marque como "admite invitados" aparece aquí, sin tocar código.
        getUpcomingEvents().then(r => setJuntas(r.data.filter(e => EVENT_TYPE_META[e.event_type]?.allows_invitados !== false))).catch(() => {});
        setLoading(false);
      })
      .catch(() => navigate('/'));
  }, [navigate]);

  const loadInvitados = () => getMyInvitados().then(res => setInvitados(res.data)).catch(() => {});

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return uiToastError('El nombre del invitado es requerido.');
    if (!form.business_name.trim()) return uiToastError('El negocio del invitado es requerido.');
    if (!form.giro.trim()) return uiToastError('El giro del negocio es requerido.');
    if (!isValidMxPhone(form.phone)) return uiToastError('Escribe un teléfono mexicano válido (10 dígitos).');
    if (!EMAIL_RE.test(form.email)) return uiToastError('Escribe un correo válido.');

    setSaving(true);
    try {
      await createInvitado(form);
      uiToastSuccess('Invitado registrado. Administración lo va a revisar.');
      setForm(EMPTY_FORM);
      loadInvitados();
    } catch (err) {
      uiToastError('Error registrando invitado: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SkeletonPage />;
  if (!profile || profile.status !== 'active') {
    return (
      <div className="page-narrow">
        <div className="state-message"><h2>Cuenta pendiente de aprobación</h2></div>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title">Invitados</h1>
      <p className="page-subtitle">Registra a los prospectos que llevas a un evento del HUB.</p>

      {/* Ancho limitado a propósito (no a todo el "page") — con el formulario a 2 columnas,
          dejarlo estirarse a los 1400px de la página se ve raro (inputs gigantes para un
          nombre o un teléfono). La tabla de abajo sí usa el ancho completo de la página. */}
      <form onSubmit={handleSubmit} className="card" style={{ marginBottom: '28px', maxWidth: '760px' }}>
        {juntas.length > 0 && (
          <div className="form-group">
            <label className="form-label">Para qué evento</label>
            <select className="form-input" value={form.event_id || ''} onChange={handleChange('event_id')}>
              <option value="">Sin especificar</option>
              {juntas.map(j => <option key={j.id} value={j.id}>{EVENT_TYPE_LABELS[j.event_type] || j.event_type} — {formatEventDate(j.event_date)}</option>)}
            </select>
          </div>
        )}
        <div className="two-col-grid">
          <div className="form-group">
            <label className="form-label">Nombre*</label>
            <input className="form-input" value={form.name} onChange={handleChange('name')} required />
          </div>
          <div className="form-group">
            <label className="form-label">Negocio*</label>
            <input className="form-input" value={form.business_name} onChange={handleChange('business_name')} required />
          </div>
        </div>
        <div className="two-col-grid">
          <div className="form-group">
            <label className="form-label">Giro del negocio*</label>
            <input className="form-input" value={form.giro} onChange={handleChange('giro')} required />
          </div>
          <div className="form-group">
            <label className="form-label">Teléfono*</label>
            <MexPhoneInput value={form.phone} onChange={(v) => setForm(prev => ({ ...prev, phone: v }))} required />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Email*</label>
          <input className="form-input" type="email" value={form.email} onChange={handleChange('email')} required />
        </div>
        <div className="form-group">
          <label className="form-label">Notas</label>
          <textarea className="form-input" rows={3} value={form.notes} onChange={handleChange('notes')} />
        </div>
        <button type="submit" className="btn btn-primary btn-block" disabled={saving}>
          {saving ? 'Guardando...' : 'Registrar invitado'}
        </button>
      </form>

      <h2 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>Tus invitados</h2>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr><th>Nombre</th><th>Negocio</th><th>Giro</th><th>Evento</th><th>Estado</th></tr>
          </thead>
          <tbody>
            {invitados.map(inv => (
              <tr key={inv.id}>
                <td>{inv.name}</td>
                <td>{inv.business_name || '—'}</td>
                <td>{inv.giro || '—'}</td>
                <td>{inv.event_type ? `${EVENT_TYPE_LABELS[inv.event_type] || inv.event_type} — ${formatEventDate(inv.event_date)}` : '—'}</td>
                <td><span className={`badge badge-${STATUS_LABELS[inv.status]?.tone || 'neutral'}`}>{STATUS_LABELS[inv.status]?.label || inv.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {invitados.length === 0 && <div className="table-empty">Aún no has registrado invitados</div>}
      </div>
    </div>
  );
}

export default Invitados;
